import {Injectable} from '@angular/core';
import {Location} from '@angular/common';
import {Router} from '@angular/router';
import {RoutingTrackerService} from './routing-tracker.service';

@Injectable({providedIn: 'root'})
export class NavigationService {
  // One-shot flag to force the very next backOr() call to use the fallback route.
  private forceNextFallback = false;
  constructor(
    private router: Router,
    private location: Location,
    private tracker: RoutingTrackerService,
  ) {
  }

  /**
   * Navigate back to previous in-app URL when available, else use browser history,
   * else attempt to close the current tab/window when it appears to be a fresh tab,
   * else navigate to the provided fallback commands with optional query params.
   */
  backOr(commandsFallback: any[] | string, queryParamsFallback?: Record<string, any>): void {
    try {
      // If explicitly requested (e.g., after Save New), always go to fallback once
      if (this.forceNextFallback) {
        this.forceNextFallback = false;
        this.navigateToFallback(commandsFallback, queryParamsFallback);
        return;
      }

      const current = this.router.url;
      const prev = this.tracker?.getPreviousUrl?.();
      // Special-case: avoid navigating back to an edit URL (with id) from a fresh "new" form state
      // Example scenario: After SaveNew on /admin/company-form we removed id from URL.
      // If prev is the same form route but with an id query, going back would reopen edit mode.
      const currentBase = typeof current === 'string' ? current.split('?')[0] : '';
      const prevBase = typeof prev === 'string' ? prev.split('?')[0] : '';
      const prevHasEditId = typeof prev === 'string' && /[?&]id=\w+/i.test(prev);
      const isSameFormRoute = !!currentBase && !!prevBase && currentBase === prevBase;
      const shouldIgnorePrevForNewForm = isSameFormRoute && prevHasEditId;

      if (prev && typeof prev === 'string' && prev !== current && !shouldIgnorePrevForNewForm) {
        this.router.navigateByUrl(prev).then();
        return;
      }
      if (window.history && window.history.length > 1 && !shouldIgnorePrevForNewForm) {
        this.location.back();
        return;
      }
    } catch {
      // ignore and fall through
    }

    // If we reach here, there is likely no meaningful SPA history in this tab.
    // This commonly happens when the page was opened in a new tab/window.
    // Best-effort: attempt to close the window. If the browser blocks it, we will
    // proceed to the safe fallback navigation below.
    try {
      // Heuristics for a fresh tab/window:
      const noHistory = !(window as any).history || (window as any).history.length <= 1;
      let crossOriginReferrer = false;
      try {
        const ref = (document as any)?.referrer as string | undefined;
        if (ref) {
          const refUrl = new URL(ref);
          crossOriginReferrer = refUrl.origin !== (window as any).location.origin;
        } else {
          // No referrer: could be direct open/new tab
          crossOriginReferrer = false;
        }
      } catch {
        // If URL parsing fails, err on the side of not blocking close attempt
        crossOriginReferrer = false;
      }
      const openedByScript = !!((window as any).opener && !(window as any).opener.closed);

      if (noHistory || openedByScript || crossOriginReferrer) {
        // Note: Some browsers only allow close() for windows opened by script.
        // Call without assuming success; we still perform a safe fallback below if it fails.
        (window as any).close();
      }
    } catch {
      // ignore
    }

    this.navigateToFallback(commandsFallback, queryParamsFallback);
  }

  /**
   * Marks that the next call to backOr() should navigate directly to the provided
   * fallback instead of attempting to return to the previous URL or history.
   * Use this right after Save New flows to ensure Close returns to list.
   */
  public forceNextBackToFallback(): void {
    this.forceNextFallback = true;
  }

  private navigateToFallback(commandsFallback: any[] | string, queryParamsFallback?: Record<string, any>): void {
    try {
      if (typeof commandsFallback === 'string') {
        this.router.navigateByUrl(commandsFallback).then();
      } else {
        const tree = this.router.createUrlTree(commandsFallback, {queryParams: queryParamsFallback});
        const url = this.router.serializeUrl(tree);
        this.router.navigateByUrl(url).then();
      }
    } catch {
      // As a last resort, go to root
      this.router.navigate(['/']).then();
    }
  }
}
